import styled from 'styled-components';
import { Translate } from 'react-redux-i18n';
import { StyleColor, Format, BaseStyledComponentProps, notchHeight, macTopBarHeight } from '../../../utils/styles-constants';
import { hexToRgba } from '../../../utils/style';
import { StyledSharePanelProps } from '../WebpublicationForm/styles';
import { spin } from '../../../utils/style';
import UserAgentUtils from '../../../utils/userAgent';

interface ButtonProps extends BaseStyledComponentProps {
	color?: string;
}

const shareFontProperties = `
	font-size: 15px;
	font-weight: 400;
	letter-spacing: 0.2px;
	font-family: 'HelveticaNeue', sans-serif;
`;

const SharePanelContainer = styled('div')`
	height: 100%;
	width: 100%;
	position: relative;
	left: 0;
	right: 0;
	bottom: 0;
	top: 0;
	z-index: 15;
	background-color: ${(props: BaseStyledComponentProps) => props.theme.primary.backgroundColor};
	transform: ${(props: StyledSharePanelProps) => props.isOpen ? 'translate(0, 0)' : 'translate(0, 100%)' };

	${UserAgentUtils.isMacApp() && `
		padding-top: ${macTopBarHeight};
		top: -${macTopBarHeight};
	`}

	${UserAgentUtils.isIOS() && `
		@media ${Format.SmartphonePortraitNotch} {
			padding-top: ${notchHeight};
			top: -${notchHeight};
		}
	`}

	@media print {
		position: absolute;
		top: 0;
		background: #fff;
		z-index: 110;
		${(props: StyledSharePanelProps) => props.isOpen ? 'translate(0, 0)' : 'display: none;' };
	}
`;

const Form = styled('form')`
	${shareFontProperties}
	display: flex;
	flex-direction: column;
	height: 100%;
	color: ${(props: BaseStyledComponentProps) => hexToRgba(props.theme.primary.strongColor, 75)};

	@media print {
		color: #000;
	}
`;

const FormHeader = styled('div')`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	flex-wrap: wrap;
	padding: 0 20px 10px 20px;

	> * {
		margin-top: 10px;
	}
`;

const SwitchLabel = styled(Translate)`
	margin-left: 20px;
	margin-right: 10px;
	font-size: 9px;
	line-height: 12px;
	color: ${(props: BaseStyledComponentProps) => props.theme.primary.strongColor};

	@media print {
		color: #000;
	}

	> span {
		font-weight: 700;
		font-size: 12px;
	}
`;

const ButtonCancel = styled('button')`
	margin-right: auto;
	padding: 0;
	border: 0;
	outline: 0;
	background-color: transparent;
	font-family: HelveticaNeue, sans-serif;
	font-weight: 500;
	font-size: 16px;
	line-height: 20px;
	color: ${({ color, theme }: ButtonProps) => color || theme.primary.actionColor};
	letter-spacing: 0.5px;
	cursor: pointer;
`;

const ButtonSend = styled('button')`
	margin-left: 20px;
	padding: 8px 25px;
	border: 0;
	border-radius: 20px;
	outline: 0;
	font-size: 15px;
	line-height: 16px;
	background-color: ${({ color, theme }: ButtonProps) => color || theme.primary.actionColor};
	color: ${StyleColor.White};
	cursor: pointer;
	white-space: nowrap;

	&:disabled {
		cursor: default;
		opacity: 0.5;
	}
`;

const FormBody = styled('div')`
	flex: 1;
	padding: 10px 30px;
	overflow-x: hidden;
	overflow-y: auto;

	@media ${Format.Tablet}, ${Format.TabletLandscape} {
		padding: 10px 45px;
	}
`;

const FormBodyInner = styled('div')`
	max-width: 900px;
	margin: 0 auto;
`;

const DocumentsBlock = styled('div')`
	display: flex;
	flex-flow: column nowrap;
`;

interface FormFieldContainerProps {
	fieldIsMessage: boolean;
	isHeader: boolean;
}

const autocompleteSpaceSize = 45;
const FormFieldContainer = styled('div')`
	display: flex;
	position: relative;
	flex-flow: row nowrap;
	border-bottom: solid 1px ${(props: BaseStyledComponentProps) => hexToRgba(props.theme.primary.strongColor, 8)};
	align-items: center;
	${(props: FormFieldContainerProps) => !props.fieldIsMessage
		? 'height: 50px;'
		: `height: 100px;
			border: none;
			@media ${Format.Smartphone} {
				height: 120px;
			}`
	};
	${(props: FormFieldContainerProps) => !props.isHeader &&
		`width: calc(100% - 15px);
		padding-left: 15px;`
	}

	:last-of-type {
		border: none;
	}

	> ul {
		left: ${autocompleteSpaceSize}px;
		width: calc(100% - ${autocompleteSpaceSize}px - 50px);

		@media only screen and (max-width: 500px) {
			width: calc(100% - 10px);
			left: 5px;
		}
	}
`;

const inputProperties = `
	width: 100%;
	height: 40px;
	${shareFontProperties}
	outline: none;
	border: none;
	background: transparent;
`;

const EmailText = styled('textarea')`
	${inputProperties}
	color: ${(props: BaseStyledComponentProps) => hexToRgba(props.theme.primary.strongColor, 75)};
	font-weight: 300px;
	resize: none;
	height: 60px;
	padding: 20px 15px;
	overflow: auto; //IE override

	@media ${Format.Smartphone} {
		height: 80px;
	}

	@media print {
		color: #000;
	}
`;

const InputField = styled('input')`
	${inputProperties}
	color: ${(props: BaseStyledComponentProps) => hexToRgba(props.theme.primary.strongColor, 75)};
	margin-left: 5px;
	max-width: calc(100% - 200px);

	@media print {
		color: #000;
	}
`;

const Loader = styled('div')`
	position: fixed;
	top: 50px;
	bottom: 0;
	left: 0;
	right: 0;
	z-index: 16;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: bold;
	font-size: 21px;
	background-color: ${(props: BaseStyledComponentProps) => hexToRgba(props.theme.primary.backgroundColor, 50)};
	color: ${(props: BaseStyledComponentProps) => props.theme.primary.strongColor};

	svg {
		height: 35px;
		width: 35px;
		animation: ${spin};
		margin-right: 20px;
	}
`;

export {
	SharePanelContainer,
	Form,
	FormHeader,
	FormBody,
	FormBodyInner,
	SwitchLabel,
	ButtonCancel,
	ButtonSend,
	FormFieldContainer,
	DocumentsBlock,
	InputField,
	shareFontProperties,
	EmailText,
	Loader,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Share/UprezForm/styles.ts